import { useCallback, useRef, useState } from 'react';

import useCamera from './useCamera';

type Source = 'camera' | 'gallery';

type UseCameraFilePickerOptions = {
  onFileSelected?: (file: File | string) => void | Promise<void>;
};

const useCameraFilePicker = (options?: UseCameraFilePickerOptions) => {
  const { takePicture } = useCamera();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uiState, setUiState] = useState({
    showCameraOptions: false,
    showGallery: false,
  });

  /** 📸 Cámara */
  const handleCamera = useCallback(async () => {
    const img = await takePicture('camera');
    if (!img) return;

    await options?.onFileSelected?.(img);
    setUiState((p) => ({ ...p, showCameraOptions: false }));
  }, [takePicture, options]);

  /** 🖼️ Galería */
  const handleGallery = useCallback(async () => {
    const img = await takePicture('gallery');
    if (!img) return;

    await options?.onFileSelected?.(img);
    setUiState((p) => ({ ...p, showCameraOptions: false }));
  }, [takePicture, options]);

  /** 📄 PDF */
  const handlePDFClick = useCallback(() => {
    setUiState((p) => ({ ...p, showCameraOptions: false }));
    fileInputRef.current?.click();
  }, []);

  const handlePDFChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await options?.onFileSelected?.(file);

      // reset para permitir seleccionar el mismo archivo
      event.target.value = '';
    },
    [options],
  );

  return {
    // UI
    showCameraOptions: uiState.showCameraOptions,
    showGallery: uiState.showGallery,
    openCameraOptions: () => setUiState((p) => ({ ...p, showCameraOptions: true })),
    closeCameraOptions: () => setUiState((p) => ({ ...p, showCameraOptions: false })),
    openGallery: () => setUiState((p) => ({ ...p, showGallery: true })),
    closeGallery: () => setUiState((p) => ({ ...p, showGallery: false })),

    // Refs
    fileInputRef,

    // Handlers
    handleCamera,
    handleGallery,
    handlePDFClick,
    handlePDFChange,
  };
};

export default useCameraFilePicker;
